import AppPagination from "@app/common/components/AppPagination";
import { Template } from "@app/common/components/Template";
import { TextField } from "@app/common/components/TextField";
import { IFiltersPagination } from "@app/common/types/http";
import { formattedDataSelect } from "@app/common/utils/utils";

import { useGetStatusList } from "@app/services/common/hooks";

import { warningNotification } from "@app/common/components/notifications";
import routes from "@app/config/routes";
import { useGetPendingConsult } from "@app/services/PendingConsult/hooks";
import { PendingConsult } from "@app/services/PendingConsult/types";
import { useDeliveryData } from "@app/store/useDeliverySearchData";
import { ActionIcon, Badge, Box, Collapse, Grid, Menu, Select, Table } from "@mantine/core";
import {
  IconBrandDeliveroo,
  IconChevronDown,
  IconChevronUp,
  IconCircleFilled,
  IconDotsVertical,
  IconEye,
  IconSearch
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import DeliveryDetailDrawer from "./DeliveryDetailDrawer";

const PendingConsults = () => {
  const navigate = useNavigate();
  const { dataDispense, setDataDispense } = useDeliveryData((store) => store);
  const [filters, setFilters] = useState<IFiltersPagination & { status: string }>({
    status: "",
    search: "",
    page: 1
  });

  const [pendingConsultList, setPendingConsultList] = useState<PendingConsult[]>([]);

  const {
    data: pendingConsultResults = {
      results: [],
      rowTotal: 0
    }
  } = useGetPendingConsult({
    ...filters,
    perpage: 10
  });

  const [FullDeliveryDrawer, setFullDeliveryDrawer] = useState({
    isOpen: false
  });

  const {
    data: statusList = {
      results: [],
      rowTotal: 0
    }
  } = useGetStatusList({
    staFunctionality: "dispensary"
  });

  useEffect(() => {
    setPendingConsultList(pendingConsultResults.results.map((res) => ({ ...res, isOpen: false })));
  }, [pendingConsultResults.results]);

  const handleAccordion = (option: (typeof pendingConsultList)[number]) => {
    const temp = pendingConsultList.map((item) =>
      item.disId === option.disId ? { ...item, isOpen: !item.isOpen } : item
    );

    setPendingConsultList(temp);
  };

  const handleSubmitFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = event.target as HTMLFormElement;

    const selectValue = target.elements.namedItem("status") as HTMLSelectElement;
    const searchValue = target.elements.namedItem("search") as HTMLInputElement;

    setFilters({
      status: selectValue.value,
      search: searchValue.value,
      page: 1
    });
  };

  const renderFiltersHeader = () => {
    return (
      <Grid
        align="flex-end"
        mb="lg"
        w="100%"
        data-testid="pendingconsults-filters"
        renderRoot={(props) => <form {...props} onSubmit={handleSubmitFilters} />}
      >
        <Grid.Col span={3}>
          <Select
            data-testid="pendingconsults-status-select"
            label="Estado"
            searchable
            data={formattedDataSelect(statusList.results, "description", "id")}
            placeholder="Seleccionar..."
            name="status"
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextField
            placeholder="Escribe aquí para buscar..."
            variant="filled"
            className="flex-grow-1 me-4"
            name="search"
            data-testid="pendingconsults-search-input"
          />
        </Grid.Col>
        <Grid.Col span={1}>
          <ActionIcon type="submit">
            <IconSearch />
          </ActionIcon>
        </Grid.Col>
      </Grid>
    );
  };

  const renderRow = (option: (typeof pendingConsultList)[number], index: number) => {
    return (
      <Fragment key={option.disId}>
        <Table.Tr data-testid={`pendingconsults-row-${index}`}>
          <Table.Td
            ta={"start"}
            w={"15rem"}
            className="flex gap-2 justify-center align-center items-center"
          >
            {option.isOpen ? (
              <ActionIcon
                className="primary-color"
                variant="subtle"
                data-testid={`pendingconsults-collapse-button-${index}`}
                onClick={() => handleAccordion(option)}
              >
                <IconChevronUp />
              </ActionIcon>
            ) : (
              <ActionIcon
                className="primary-color"
                variant="subtle"
                data-testid={`pendingconsults-expand-button-${index}`}
                onClick={() => handleAccordion(option)}
              >
                <IconChevronDown />
              </ActionIcon>
            )}
            <Box w={"100%"}>{option.patient.name}</Box>
          </Table.Td>

          <Table.Td ta={"center"}>
            {option.patient.documentType} {option.patient.document}
          </Table.Td>
          <Table.Td>{option.modality || "-"}</Table.Td>
          <Table.Td ta={"center"}>{option.patient.phone || "-"}</Table.Td>
          <Table.Td ta={"center"}>{option?.morDate?.split("T")[0] || "-"}</Table.Td>
          <Table.Td ta={"center"}>{option?.validity_date?.split("T")[0] || "-"}</Table.Td>
          <Table.Td>{option.orderedBy.name || "-"}</Table.Td>
          <Table.Td>
            {" "}
            <Badge variant="light" color={option.status.fontColor} w={"100%"}>
              {option.status.description}
            </Badge>
          </Table.Td>
          <Table.Td ta={"center"}>
            <Menu position="bottom-end" withArrow data-testid={`pendingconsults-menu-${index}`}>
              <Menu.Target>
                <ActionIcon variant="subtle">
                  <IconDotsVertical />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconBrandDeliveroo />}
                  data-testid={`pendingconsults-deliver-item-${index}`}
                  onClick={() => {
                    setDataDispense({
                      ...dataDispense,
                      fromPendingOrder: true,
                      patientId: Number(option.patient.id ?? 0),
                      deliveryBy: "insurance",
                      orderId: option.morId
                    });
                    navigate(routes.getDeliveryDispensation);
                  }}
                >
                  Entregar
                </Menu.Item>
                <Menu.Item
                  leftSection={
                    <IconCircleFilled
                      style={{
                        width: 10
                      }}
                    />
                  }
                  data-testid={`pendingconsults-ordering-item-${index}`}
                  onClick={() => warningNotification("En desarrollo")}
                >
                  Ordenamiento
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconEye />}
                  data-testid={`pendingconsults-detail-item-${index}`}
                  onClick={() => {
                    setFullDeliveryDrawer((state) => ({
                      ...state,
                      isOpen: true
                    }));
                    warningNotification("En desarrollo");
                  }}
                >
                  Ver detalle
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Table.Td>
        </Table.Tr>

        <Table.Tr
          style={{ borderBottom: option.isOpen ? "1px solid #dee2e6" : 0 }}
          data-testid={`pendingconsults-collapse-row-${index}`}
        >
          <Table.Td py={0} colSpan={12}>
            <Collapse in={option.isOpen}>
              <Box px="45px" py={"sm"} data-testid={`pendingconsults-medicines-container-${index}`}>
                <table style={{ width: "60%", borderBottom: 0 }}>
                  <thead>
                    <tr className="primary-color">
                      <th align="left">Medicamento</th>
                      <th align="center">Descripción</th>
                      <th align="center">Cantidad</th>
                      <th align="center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {option?.medicines?.length > 0 &&
                      option?.medicines?.map((medicine, medicineIndex) => {
                        return (
                          <tr
                            key={`${medicine.id}-${medicineIndex}`}
                            data-testid={`pendingconsults-medicine-row-${medicineIndex}`}
                          >
                            <td>{medicine.name}</td>
                            <td align="center">{medicine.presentation.name}</td>
                            <td align="center">{medicine.qty_delivered}</td>
                            <td align="center">
                              {" "}
                              <Badge variant="light" color={option.status.fontColor} w={"100%"}>
                                {option.status.description}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </Box>
            </Collapse>
          </Table.Td>
        </Table.Tr>
      </Fragment>
    );
  };

  const renderTable = () => {
    return (
      <Table className="biowel-table" data-testid="pendingconsults-table">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Paciente</Table.Th>
            <Table.Th ta={"center"}>Identificación</Table.Th>
            <Table.Th ta={"start"}>Modalidad</Table.Th>
            <Table.Th ta={"center"}>Teléfono</Table.Th>
            <Table.Th ta={"center"}>Fecha orden</Table.Th>
            <Table.Th ta={"center"}>Fecha validez</Table.Th>
            <Table.Th ta={"start"}>Ordenado por</Table.Th>
            <Table.Th ta={"center"}>Estado</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {pendingConsultList?.length ? (
            pendingConsultList?.map((item, index) => renderRow(item, index))
          ) : (
            <Table.Tr>
              <Table.Td ta="center" colSpan={12} data-testid="pendingconsults-no-data">
                No hay información para mostrar.
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
    );
  };

  return (
    <Template title="Consultas pendientes">
      {renderFiltersHeader()}
      {renderTable()}
      <DeliveryDetailDrawer
        onClose={() => {
          setFullDeliveryDrawer((state) => ({
            ...state,
            isOpen: false
          }));
        }}
        isOpen={FullDeliveryDrawer.isOpen}
      />
      <AppPagination
        total={pendingConsultResults?.rowTotal || 0}
        rowTotal={pendingConsultResults?.rowTotal || 0}
        value={filters.page || 1}
        onChange={(value) => setFilters((state) => ({ ...state, page: value }))}
        data-testid="pendingconsults-pagination"
      />
    </Template>
  );
};

export default PendingConsults;
